import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import Input from '~/bootcamp/Activities/components/Input'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generatePizzaParts, toImageSrcKeys } from './FractionPizza.utils'
import CountedSlicesOutline from './CountedSlicesOutline'

const MIN_NUMERATORS = [1, 1, 1, 1, 1]
const MAX_NUMERATORS = [4, 4, 4, 6, 9]
const MAX_DENOMINATORS = [5, 5, 5, 7, 10]
const MAX_HISTORY_LENGTH = [3, 3, 3, 4, 5]
const IS_PIECES_TOGETHER = [1, 1, 1, 0, 0]
// Every pizza task asks for both halves except FractionPizza2's level 2 ("Skriv totalt antall deler pizzaen
// har blitt delt inn i, som nevneren"), which asks for the denominator alone and is given the numerator.
const NUMERATOR_GIVEN_TYPE = 2
const NUMERATOR_GIVEN_LEVEL = 2
const mediaQuery = [{ screen: '(min-width: 280px)', width: '128px' }]

function FractionPizza({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC: SubmitButton,
  type,
}: ActivityProps & { type: number }): React.ReactElement {
  const { t } = useTranslation('generics')
  const isNumeratorGiven = type === NUMERATOR_GIVEN_TYPE && difficultyLevel === NUMERATOR_GIVEN_LEVEL
  const [answer, setAnswer] = useState({ denominator: '', numerator: '' })
  const [imagesSrcKeys, setImagesSrcKeys] = useState<string[]>([])
  const [outlinedParts, setOutlinedParts] = useState<number[]>([])
  const [numeratorHistory, setNumeratorHistory] = useState<number[]>([])
  const [{ denominator, numerator, lines }, setNumbers] = useState({
    denominator: 1,
    numerator: 1,
    lines: [0],
  })
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    const generatedNumbers = generateDenominatorNumerator(
      MIN_NUMERATORS[difficultyLevel - 1],
      MAX_NUMERATORS[difficultyLevel - 1],
      2,
      MAX_DENOMINATORS[difficultyLevel - 1],
      numeratorHistory,
    )
    setNumbers({ ...generatedNumbers, lines: new Array(generatedNumbers.denominator).fill(0) })
    const { shownParts, missingParts } = generatePizzaParts(
      generatedNumbers.denominator,
      generatedNumbers.numerator,
      Boolean(IS_PIECES_TOGETHER[difficultyLevel + 1]),
      type,
    )
    setImagesSrcKeys(toImageSrcKeys(generatedNumbers.denominator, shownParts))
    // The numerator counts the missing slices on type 1 and the remaining ones on type 2 — outline whichever it is.
    setOutlinedParts(type === 1 ? missingParts : shownParts)
    setAnswer({ denominator: '', numerator: isNumeratorGiven ? generatedNumbers.numerator.toString() : '' })
  }, [numeratorHistory, difficultyLevel, type, isNumeratorGiven])

  // Where the numerator is given it is painted beside the box as plain text, so it belongs in the task and
  // only the denominator rides the guarded channel.
  const paintedTask = `a pizza figure; \\frac{${isNumeratorGiven ? numerator : '\\square'}}{\\square}`
  const guardedValue = isNumeratorGiven ? denominator.toString() : `\\frac{${numerator}}{${denominator}}`
  useEmitInstance(onInstanceChange, denominator > 1 ? paintedTask : null, denominator > 1 ? guardedValue : null)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer.denominator === denominator.toString() && answer.numerator === numerator.toString()
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer({ denominator: '', numerator: isNumeratorGiven ? numerator.toString() : '' })
  }

  const handleOnNextQuestionClick = (): void => {
    setNumeratorHistory((prevState) => {
      return [numerator, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1])
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  function renderLines(): React.ReactElement[] | unknown {
    // On type 1 the empty wedges are the counted ones, so the outline already divides the empty
    // space and a dashed line on top of it just doubles up. On type 2 nothing else divides it, and
    // the denominator asks how many parts there are, so the dividers have to stay.
    if (type === 1) return null

    return lines.map((item, index) => {
      // The divider at `index` separates part `index` from part `index + 1`, wrapping round at 12
      // o'clock. A slice draws its own edge, so the dashed line is only needed between two gaps.
      const neighbours = toImageSrcKeys(denominator, [index === 0 ? denominator : index, index + 1])
      const shouldRender = neighbours.every((srcKey) => !imagesSrcKeys.includes(srcKey))
      return shouldRender ? (
        <ResponsiveImage
          alt="Dashed line"
          height={128}
          imageClassName="absolute"
          imageWidths="128"
          key={index.toString() + 1}
          mediaQuery={mediaQuery}
          srcKey="bootcamp/pizza/pizza-line.png"
          style={{
            transform: `rotate(${360 - (360 / lines.length) * index}deg)`,
          }}
          width={128}
        />
      ) : null
    })
  }
  const onEnterPress = (): void => {
    if (answer.denominator !== '' && answer.numerator !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="">
        <div className="relative h-32 w-32">
          <ResponsiveImage
            alt="Pizza background"
            height={128}
            imageClassName="absolute"
            imageWidths="100, 130"
            mediaQuery={mediaQuery}
            srcKey="bootcamp/pizza/pizza-back.png"
            width={128}
          />
          {renderLines()}
          {imagesSrcKeys.map((imageSrcKey) => (
            <ResponsiveImage
              alt={imageSrcKey}
              height={128}
              imageClassName="absolute"
              imageWidths="130"
              key={imageSrcKey}
              mediaQuery={mediaQuery}
              srcKey={imageSrcKey}
              width={128}
            />
          ))}
          <CountedSlicesOutline denominator={denominator} hasSliceArt={type !== 1} parts={outlinedParts} />
        </div>
        <div className="ml-4 flex flex-col items-center">
          {isNumeratorGiven ? (
            <span data-test-id="pizza-given-numerator">{numerator}</span>
          ) : (
            <Input
              aria-label={t('ariaLabelButtons.insertNumerator')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={`${inputKey}-first`}
              maxDigits={4}
              onChange={(value) => setAnswer((state) => ({ denominator: state.denominator, numerator: value }))}
              onEnter={onEnterPress}
              ref={inputRef}
              value={answer.numerator}
            />
          )}
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            autoFocus={autofocus && isNumeratorGiven}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: value, numerator: state.numerator }))}
            onEnter={onEnterPress}
            ref={isNumeratorGiven ? inputRef : null}
            value={answer.denominator}
          />
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid={answer.denominator !== '' && answer.numerator !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FractionPizza
